import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import seoData from '../seo/about.json';
import './About.css';

const values = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
    ),
    title: 'Client-Centered',
    desc: 'We build close relationships and provide guidance tailored to your unique financial needs.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
    ),
    title: 'Proactive Approach',
    desc: 'We look beyond the present to help you plan and prepare for a financially secure future.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
    ),
    title: 'Excellence',
    desc: 'High standards of professionalism in every service we deliver, backed by thorough research and analysis.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
    ),
    title: 'Continuous Learning',
    desc: 'We stay current on evolving tax laws and financial regulations to serve you better.',
  },
];

export default function About() {
  return (
    <>
      <SEOHead seo={seoData} />

      {/* ===== PAGE HEADER ===== */}
      <section className="page-header">
        <div className="page-header__bg" />
        <div className="container">
          <div className="page-header__content">
            <span className="section-label" style={{ color: 'var(--gold-light)' }}>About Us</span>
            <h1 className="page-header__title">A Legacy of Trust &amp; Expertise</h1>
            <p className="page-header__subtitle">
              Providing quality, personalized financial guidance to individuals and businesses for over 30 years.
            </p>
          </div>
        </div>
      </section>

      {/* ===== STORY ===== */}
      <section className="section about-story">
        <div className="container">
          <div className="about-story__layout">
            <div className="about-story__content">
              <span className="section-label">Our Story</span>
              <h2 className="section-title">Building Lasting Financial Relationships</h2>
              <p>
                For over 30 years, Gelejian Financial Services has been a trusted partner
                for individuals and businesses seeking quality, personalized financial guidance.
                Founded by Vahan Gelejian, our office has grown from a commitment to treating every
                client as a valued partner, not just another account number.
              </p>
              <p>
                Our expertise spans from fundamental tax management and accounting services to 
                more advanced solutions including audit assistance, payroll processing, financial statements,
                and comprehensive financial planning. We serve clients throughout Southern California and beyond.
              </p>
              <p>
                What sets us apart is our dedication to open communication and thorough research. 
                We take the time to understand each client's unique situation, goals, and concerns
                — then craft strategies that deliver real results.
              </p>
            </div>
            <div className="about-story__image">
              <div className="about-story__img-wrapper">
                <div className="about-story__graphic">
                  <div className="about-story__years">
                    <span className="about-story__years-num">30+</span>
                    <span className="about-story__years-text">Years of<br />Excellence</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MISSION ===== */}
      <section className="section about-mission">
        <div className="container">
          <div className="about-mission__card">
            <span className="section-label" style={{ color: 'var(--gold-light)' }}>Our Mission</span>
            <h2 style={{ color: 'var(--white)', marginBottom: '20px' }}>
              Financial Viability Today, Security Tomorrow
            </h2>
            <p className="about-mission__text">
              Our mission is to help clients maintain financial viability in the present while 
              taking a proactive approach to achieve future goals. This requires open communication 
              to reach an understanding of our clients' needs, thorough research, and sound analysis. 
              Gelejian Financial Services is dedicated to meeting these goals with high standards of 
              excellence and professionalism.
            </p>
          </div>
        </div>
      </section>

      {/* ===== VALUES ===== */}
      <section className="section about-values">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <span className="section-label">Our Values</span>
            <h2 className="section-title">What Drives Us Every Day</h2>
          </div>
          <div className="about-values__grid">
            {values.map((v, i) => (
              <div key={i} className="value-card fade-in-up" style={{ animationDelay: `${0.1 + i * 0.1}s` }}>
                <div className="value-card__icon">{v.icon}</div>
                <h3 className="value-card__title">{v.title}</h3>
                <p className="value-card__desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TEAM ===== */}
      <section className="section about-team" style={{ background: 'var(--sky-pale)' }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <span className="section-label">Our Team</span>
            <h2 className="section-title">Meet Your Financial Partner</h2>
          </div>
          <div className="about-team__grid">
            <div className="about-team__card">
              <div className="about-team__avatar">
                <span>VG</span>
              </div>
              <div className="about-team__info">
                <h3>Vahan Gelejian</h3>
                <span className="about-team__role">Founder and CEO</span>
                <p>
                  With over 30 years of experience in accounting and financial services,
                  Vahan Gelejian founded Gelejian Financial Services with a vision to provide
                  personalized, high-quality financial guidance to individuals and businesses.
                  His expertise spans tax management, accounting, financial planning, and business consulting.
                </p>
                <div className="about-team__contact-info">
                  <a href="mailto:vahan@gelejianfs.com">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    vahan@gelejianfs.com
                  </a>
                  <a href="tel:+18183430080">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    (818) 343-0080
                  </a>
                </div>
              </div>
            </div>

            <div className="about-team__card">
              <div className="about-team__avatar">
                <span>MG</span>
              </div>
              <div className="about-team__info">
                <h3>Michelle Gelejian</h3>
                <span className="about-team__role">Accounting Clerk</span>
                <p>
                  Michelle Gelejian supports the team with meticulous bookkeeping, data entry,
                  and day-to-day accounting operations, ensuring every client's records are
                  accurate and up to date.
                </p>
                <div className="about-team__contact-info">
                  <a href="mailto:accounting@gelejianfs.com">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    accounting@gelejianfs.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="section text-center" style={{ paddingBottom: '100px' }}>
        <div className="container">
          <h2 className="section-title">Ready to Work Together?</h2>
          <p className="section-subtitle" style={{ margin: '0 auto 32px' }}>
            Let us help you navigate your financial journey with confidence.
          </p>
          <Link to="/contact" className="btn btn-primary btn--lg">
            Contact Us Today
          </Link>
        </div>
      </section>
    </>
  );
}
